import {
  Icon,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuLinkItem,
  MenuSeparator,
  MenuShortcut,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Text,
} from "@salestrak/ui";
import React from "react";
import { CHANGELOG_URL } from "#app/lib/marketing-urls";
import {
  formatDeviceLabel,
  resolveDeviceContext,
} from "#app/lib/analytics/device-context";

function useClientHintsVersion(): string | null {
  const [version, setVersion] = React.useState<string | null>(null);
  React.useEffect(() => {
    // @ts-expect-error -- userAgentData is not yet in all TS DOM libs
    navigator.userAgentData
      ?.getHighEntropyValues(["platformVersion"])
      .then((hints: { platformVersion?: string }) => {
        if (hints.platformVersion) setVersion(hints.platformVersion);
      })
      .catch(() => {
      });
  }, []);
  return version;
}

/**
 * Help sub-menu for the user avatar menu. Renders ONLY the menu — any dialog it
 * opens (e.g. the feedback dialog, via `onSendFeedback`) must be mounted by a
 * parent that stays mounted when the menu closes (e.g. `SiteHeader`). Pressing a
 * `MenuItem` closes the dropdown and unmounts everything under the popup, so a
 * dialog rendered here would unmount before it could ever show.
 */
export function HelpSubMenu({
  onSendFeedback,
}: {
  onSendFeedback: () => void;
}) {
  const clientHintsVersion = useClientHintsVersion();
  const deviceInfo = React.useMemo(
    () => formatDeviceLabel(resolveDeviceContext(clientHintsVersion)),
    [clientHintsVersion],
  );

  return (
    <MenuSub>
      <MenuSubTrigger>
        <Icon color="muted" name="help-circle" size="md" />
        <Text>Help</Text>
      </MenuSubTrigger>
      <MenuSubPopup>
        <MenuGroup>
          <MenuItem disabled>
            <Icon color="muted" name="keyboard" size="sm" />
            <Text>Shortcuts</Text>
          </MenuItem>
          <MenuItem disabled>
            <Icon color="muted" name="book-open" size="sm" />
            <Text>Docs</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuItem>
          <MenuItem disabled>
            <Icon color="muted" name="file-code" size="sm" />
            <Text>API Docs</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuItem>
          <MenuItem onClick={onSendFeedback}>
            <Icon color="muted" name="message-square" size="sm" />
            <Text>Send feedback</Text>
          </MenuItem>
          <MenuItem disabled>
            <Icon color="muted" name="pen-line" size="sm" />
            <Text>Contact us</Text>
          </MenuItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuGroupLabel>What's new</MenuGroupLabel>
          {/* A real anchor — the link opens through the element. */}
          <MenuLinkItem
            href={CHANGELOG_URL}
            rel="noopener noreferrer"
            target="_blank"
          >
            <Icon color="muted" name="scroll-text" size="sm" />
            <Text>Full changelog</Text>
            <Icon
              className="ms-auto"
              color="muted"
              name="external-link"
              size="sm"
            />
          </MenuLinkItem>
        </MenuGroup>
        <MenuSeparator />
        <MenuGroup>
          <MenuItem>
            <Icon color="muted" name="monitor" size="sm" />
            <Text truncate>Device info</Text>
            <MenuShortcut className="truncate">{deviceInfo}</MenuShortcut>
          </MenuItem>
        </MenuGroup>
      </MenuSubPopup>
    </MenuSub>
  );
}
